// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { xngBannerStyles } from "@rpsc/App/Widgets/XngBanner/XngBanner";
import { LazyImageStyle } from "@rpsc/MobiusOverrides/LazyImage";
import { fontConfig } from "@rpsc/XDL/PreStyle/typography";
import { xScreen } from "@rpsc/XDL/Utilities";
import { css } from "styled-components";

xngBannerStyles.container = {
    css: css`
        ${({
            backgroundColor,
            reverseContainer,
            containerPaddingForLargeScreens,
            containerPaddingForSmallScreens,
            containerGapForSmallScreens,
        }: any) => {
            return css`
                display: flex;
                position: relative;
                background: ${backgroundColor};
                flex-direction: ${reverseContainer ? "row-reverse" : "row"};
                gap: 32px;
                padding: ${containerPaddingForLargeScreens};
                .banner-content {
                    z-index: 3;
                    width: 100%;
                }

                ${({ maxImageContentHeight, imageContent }: any) =>
                    !imageContent &&
                    css`
                        min-height: ${maxImageContentHeight || "283px"};
                    `};
                ${LazyImageStyle} {
                    ${({ imgBorderRadius = "0px" }: any) =>
                        css`
                            border-radius: ${imgBorderRadius};
                            overflow: hidden;
                        `}
                }

                ${xScreen("lte", "md")} {
                    flex-direction: column;
                    min-height: unset;
                    padding: ${containerPaddingForSmallScreens};
                    gap: ${containerGapForSmallScreens};
                }
            `;
        }}
    `,
};

xngBannerStyles.backgroundImage = {
    css: css`
        width: 100%;
        height: 100%;
        position: absolute;
        min-height: ${({ maxImageContentHeight }: any) => maxImageContentHeight || "283px"};
        top: 0;
        left: 0;

        picture {
            width: 100%;
            display: flex;
            height: 100%;

            img {
                width: 100%;
            }
        }

        ${xScreen("lte", "md")} {
            position: relative;
            picture {
                min-height: 100%;
            }
        }
    `,
};

xngBannerStyles.imageContent = {
    css: css`
        ${({ maxImageContentHeight, imageContentPosition }: any) => {
            return css`
                width: 100%;
                height: ${maxImageContentHeight};
                img {
                    max-width: 100%;
                    max-height: 100%;
                    object-fit: contain;
                }

                ${xScreen("lte", "md")} {
                    height: auto;
                }
                ${imageContentPosition === "right" && `order: 1`}
            `;
        }};
    `,
};

xngBannerStyles.contentDetailsWrapper = {
    css: css`
        ${({
            contentPaddingForLargeScreens,
            contentPaddingForSmallScreens,
            contentMarginForLargeScreens,
            contentMarginForSmallScreens,
            contentBackgroundColor,
            contentBorderRadius,
            textContentMaxWidth,
            contentAlignmentLargeScreens,
            contentAlignmentSmallScreens,
        }: any) => {
            return css`
                display: flex;
                flex-direction: column;
                justify-content: ${contentAlignmentLargeScreens};
                padding: ${contentPaddingForLargeScreens};
                margin: ${contentMarginForLargeScreens};
                background: ${contentBackgroundColor};
                border-radius: ${contentBorderRadius};
                max-width: ${textContentMaxWidth};
                z-index: 3;
                ${xScreen("lte", "md")} {
                    padding: ${contentPaddingForSmallScreens};
                    margin: ${contentMarginForSmallScreens};
                    justify-content: ${contentAlignmentSmallScreens};
                    border-radius: 0;
                    width: 100%;
                    max-width: 100%;
                }
            `;
        }}
    `,
};

xngBannerStyles.title = {
    variant: "h1",
    forwardAs: "span",
    css: css`
        ${TypographyStyle} {
            font-size: 28px;
            line-height: 38px;
            font-weight: ${fontConfig.weight.bold};
            margin-bottom: 0;
        }
        margin-bottom: 12px;
    `,
};

xngBannerStyles.subTitle = {
    variant: "h3",
    forwardAs: "span",
    css: css`
        ${TypographyStyle} {
            font-size: 20px;
            line-height: 28px;
            font-weight: ${fontConfig.weight.bold};
            margin-bottom: 0;
        }
        margin-bottom: 8px;
    `,
};

xngBannerStyles.button = {
    css: css`
        width: fit-content;
    `,
};
